import React, { useMemo } from 'react';
import { emptyArray } from '@fabric-ui/utils';
import { useAvContext } from '../context/AvContextProvider';
import { useGetDisplayName } from '../views/CustomDashboards/hooks/useGetDisplayName';
import { useGetFieldType } from '../views/CustomDashboards/hooks/useGetFieldType';
import AvTable from './Table/AvTable';
import { isNumberType } from './Table/table.utils';
import { AvTableProps, FormatterType } from './Table/types';
import WithDescriptionTooltip from './WithDescriptionTooltip/WithDescriptionTooltip';

interface AvProjTableProps {
  activeProjName: string;
  headCellsKey?: string;
  shouldPreferLabelAsCellTitle?: boolean;
}

const AvProjTable: React.FC<AvTableProps & AvProjTableProps> = ({
  headCells = emptyArray,
  activeProjName,
  headCellsKey,
  totalProps,
  shouldPreferLabelAsCellTitle = false,
  ...rest
}) => {
  const getHeadCellType = useGetFieldType(activeProjName);
  const getFieldDisplayName = useGetDisplayName(activeProjName);
  const { measurements } = useAvContext();

  const adjustedHeadCells = useMemo(() => {
    const isKeyIncludedInHeadCells = !!headCellsKey && headCells.some(({ id }) => id === headCellsKey);
    return [
      ...(headCellsKey && !isKeyIncludedInHeadCells
        ? [
            {
              id: headCellsKey,
              label: 'ID',
              hidden: true,
              isKey: true,
            },
          ]
        : []),
      ...headCells.map(v => ({ ...v, isKey: v.isKey || (isKeyIncludedInHeadCells && v.id === headCellsKey) })),
    ].map(cell => {
      const displayName = getFieldDisplayName(cell.id) || cell.id;

      return {
        ...cell,
        label: (
          <WithDescriptionTooltip title={measurements.asObject[cell.id]?.description} placement="top">
            <span>{shouldPreferLabelAsCellTitle ? cell.label || displayName : displayName}</span>
          </WithDescriptionTooltip>
        ),
        type: cell.type || (getHeadCellType({ entityFieldKey: cell.id }) as FormatterType),
        ...(cell.fieldName && { fieldName: cell.fieldName }),
      };
    });
  }, [headCellsKey, headCells, activeProjName, measurements.asObject]);

  const adjustedTotalProps = {
    ...totalProps,
    cells: totalProps?.cells
      ? totalProps.cells.map(({ id }) => {
          const type = getHeadCellType({ entityFieldKey: id });
          return {
            id,
            type: isNumberType(type) ? type : 'string',
          };
        })
      : undefined,
  };

  return <AvTable {...rest} headCells={adjustedHeadCells} totalProps={adjustedTotalProps} />;
};

export default AvProjTable;
